/**
 * 工具数据缓存系统
 * 实现预生成数据的快速查询
 */

import { safeJsonParse, sanitizeCacheKey } from './sanitize';

// 单个工具的数据结构
interface CachedTool {
  name: string;
  category: string;
  coreUsage: string;
  corePositioning: string;
  installation: {
    ubuntu: string;
    centos: string;
    docker: string;
    macos: string;
  };
  downloadUrl: {
    mirror: string;
    official: string;
  };
  commonIssues: Array<{
    rank: number;
    problem: string;
    solution: string;
  }>;
  commonCommands: Array<{
    command: string;
    description: string;
  }>;
  rating: number;
  applicableScenarios: string;
  coreAdvantages: string[];
  alternatives: string[];
  tags: string[];
}

// 完整的搜索结果结构
interface CachedSearchResult {
  results?: CachedTool[];
  zh?: { results?: CachedTool[] };
  en?: { results?: CachedTool[] };
  searchIntent?: string;
  searchTime?: string;
  resultCount?: number;
  relatedTools?: any[];
  correctedQuery?: string;
  fromCache?: boolean;
  debugInfo?: any;
  [key: string]: any;  // 允许其他字段
}

interface CacheEntry {
  keyword: string;  // 搜索关键词
  data: CachedSearchResult;  // 改为 data，可以是完整结果或单个工具
  createdAt: number;
  hitCount: number;
}

// 缓存存储
const CACHE_STORAGE_KEY = 'psp_tools_cache';
const SEARCH_HISTORY_KEY = 'psp_search_history';

/**
 * 从缓存获取工具数据
 */
export function getFromCache(keyword: string): CachedSearchResult | null {
  try {
    const cacheJson = localStorage.getItem(CACHE_STORAGE_KEY);
    if (!cacheJson) return null;

    const cache: Record<string, any> = safeJsonParse(cacheJson, {});

    // 使用安全的缓存键
    const safeKey = sanitizeCacheKey(keyword);
    const entry = cache[safeKey];

    if (entry) {
      // 更新命中次数
      entry.hitCount++;
      cache[safeKey] = entry;
      localStorage.setItem(CACHE_STORAGE_KEY, JSON.stringify(cache));

      // 兼容旧格式：如果有 tool 字段，自动转换为 data
      if (entry.tool && !entry.data) {
        console.log(`🔄 检测到旧缓存格式，自动迁移: ${keyword}`);
        // 将旧格式的工具对象包装成新的搜索结果格式
        entry.data = {
          results: [entry.tool],
          searchIntent: entry.tool.category || '工具',
          fromCache: true
        };
        // 保存迁移后的数据
        cache[safeKey] = entry;
        localStorage.setItem(CACHE_STORAGE_KEY, JSON.stringify(cache));
      }

      console.log(`✅ 缓存命中: ${keyword}`);
      return entry.data;
    }

    return null;
  } catch (error) {
    console.error('缓存读取失败:', error);
    return null;
  }
}

/**
 * 保存到缓存
 */
export function saveToCache(keyword: string, data: CachedSearchResult): void {
  try {
    const cacheJson = localStorage.getItem(CACHE_STORAGE_KEY) || '{}';
    const cache: Record<string, CacheEntry> = safeJsonParse(cacheJson, {});

    // 使用安全的缓存键
    const safeKey = sanitizeCacheKey(keyword);
    cache[safeKey] = {
      keyword: safeKey,
      data: data,
      createdAt: Date.now(),
      hitCount: 0
    };

    localStorage.setItem(CACHE_STORAGE_KEY, JSON.stringify(cache));
    console.log(`💾 已缓存: ${keyword}`);
  } catch (error) {
    console.error('缓存保存失败:', error);
  }
}

/**
 * 获取所有搜索历史（用于批量生成）
 */
export function getSearchHistory(): string[] {
  try {
    const historyJson = localStorage.getItem(SEARCH_HISTORY_KEY);
    return historyJson ? safeJsonParse(historyJson, []) : [];
  } catch (error) {
    console.error('读取历史失败:', error);
    return [];
  }
}

/**
 * 记录搜索历史
 */
export function recordSearch(keyword: string): void {
  try {
    const history = getSearchHistory();
    const safeKeyword = sanitizeCacheKey(keyword);
    if (!history.includes(safeKeyword)) {
      history.push(safeKeyword);
      localStorage.setItem(SEARCH_HISTORY_KEY, JSON.stringify(history));
    }
  } catch (error) {
    console.error('记录历史失败:', error);
  }
}

/**
 * 获取热门搜索（用于批量生成优先级）
 */
export function getPopularSearches(minCount = 2): Array<{ keyword: string; count: number }> {
  const cacheJson = localStorage.getItem(CACHE_STORAGE_KEY);
  if (!cacheJson) return [];

  const cache: Record<string, CacheEntry> = safeJsonParse(cacheJson, {});
  const results = Object.values(cache)
    .filter(entry => entry.hitCount >= minCount)
    .map(entry => ({
      keyword: entry.keyword,
      count: entry.hitCount
    }))
    .sort((a, b) => b.count - a.count);

  return results;
}

/**
 * 获取所有缓存数据（用于调试和批量导出）
 */
export function getAllCacheData(): Record<string, CachedSearchResult> {
  try {
    const cacheJson = localStorage.getItem(CACHE_STORAGE_KEY);
    if (!cacheJson) return {};

    const cache: Record<string, CacheEntry> = safeJsonParse(cacheJson, {});

    // 返回所有缓存的数据（兼容旧格式）
    const result: Record<string, CachedSearchResult> = {};
    for (const [key, entry] of Object.entries(cache)) {
      result[key] = entry.data;
    }

    return result;
  } catch (error) {
    console.error('读取所有缓存失败:', error);
    return {};
  }
}

/**
 * 清除缓存
 */
export function clearCache(): void {
  localStorage.removeItem(CACHE_STORAGE_KEY);
  console.log('🗑️ 缓存已清除');
}
